/* eslint-disable react/no-danger */
import React, { useEffect, useState } from "react";
import Link from "next/link";
import axios from "axios";
import Head from "next/head";
import dynamic from "next/dynamic";
import styled from "styled-components";
import { animateScroll as scroll } from "react-scroll";
import Collapsible from "react-collapsible";
import sanitizeHtml from "sanitize-html";
import { WordPressAPI } from "../config";
import AboutServiceComponent from "@/components/AboutService/AboutServiceComponent";
import FooterComponent from "@/components/Footer/FooterComponent";
import TestimonialComponent from "@/components/Testimonial/TestimonialComponent";
import { PricingBStyles } from "@/components/styles/PricingBStyles";
import { isOfficeHours } from "../services/isOfficeHours";
import { formatPhone } from "../services/formatPhone";
import Header from "@/components/Header/Header";
import SEOHead from "@/components/SEOHead/SEOHead";
import validatePaidCampaign from "@helpers/validatePaidCampaign";
import Image from "next/future/image";
import ToolTip from "@components/Tooltip";
import Cookies from 'js-cookie';

const FooterNewsModal = dynamic(import('@/components/FooterNewsModal'));

const TrustPilotComponent = dynamic(
  () => import("@/components/TrustPilot/TrustPilotComponent"),
  { ssr: false }
);

const PullUpScheduler = dynamic(() =>
  import("@/components/PullUpScheduler/PullUpSchedulerV2")
);

const TPStyles = styled.div`
  background-color: #fff;
  padding: 20px 0;
  position: relative;
  .tp-container {
    margin: 0 auto;
    max-width: 100%;
  }
`;

const sanitizeSettings = {
  allowedTags: [],
};

const PricingCardStyles = styled.div`
  background: #ffffff 0% 0% no-repeat padding-box;
  box-shadow: 0px 3px 6px #00000029;
  border: 1px solid #e0e0e0;
  border-radius: 3px;
  padding: 2rem 1rem;
  text-align: center;
  img {
    min-width: 76px;
  }
`;

const PricingPage = ({ data }) => {
  const {
    about_services: aboutServices,
    q_and_a: faqs,
    faq_title: faqTitle,
    meta_description: metaDescription,
    packages_header_title: packagesHeaderTitle,
    packages,
    hero_title: heroTitle,
    hero_sub_title: heroSubtitle,
    lead_values: leadValues,
    page_title_tag: pageTitleTag,
    testimonial,
    pricing_value_props: valueProps,
    phone_number_setup,
    additional_questions,
    costume_package: costumePackage,
  } = data.acf;

  const paid_campaign = validatePaidCampaign(data.acf.paid_campaign);
  const [isSchedulerOpen, setIsSchedulerOpen] = useState(false);
  const [currentPackage, setCurrentPackage] = useState("");
  const [hideSchedulerHeader, setHideSchedulerHeader] = useState(false);

  useEffect(() => {
    if (isSchedulerOpen) {
      setHideSchedulerHeader(true);
    } else {
      setHideSchedulerHeader(false);
    }
  }, [isSchedulerOpen])

  useEffect(() => {
    let isMounted = true;
    if (isMounted) {
      return isSchedulerOpen
        ? document.body.classList.add("no-overflow")
        : document.body.classList.remove("no-overflow");
    }

    return () => {
      isMounted = false;
    };
  }, [isSchedulerOpen]);

  const scrollUp = () => {
    scroll.scrollToTop({
      duration: 250,
    });
  };

  const selectPackage = () => {
    // append acc_package value to utm-content
    const accPackage = sessionStorage.getItem("acc_package");
    const query = JSON.parse(localStorage.getItem("query"));
    const utmContent = query.utm_content_base || (query || {}).utm_content;

    const packageArray = sessionStorage.getItem("acc_package").split(" ");
    const packageName = packageArray[packageArray.length - 1];
    setCurrentPackage(packageName);

    if (accPackage && utmContent) {
      const newUtmContent = utmContent
        .split(",")
        .map((u) => `${u.trim()}-${accPackage}`)
        .join(", ");

      localStorage.setItem(
        "query",
        JSON.stringify({
          ...query,
          utm_content: newUtmContent,
          utm_content_base: utmContent,
        })
      );
    }
    setIsSchedulerOpen(!isSchedulerOpen);
  };

  return (
    <>
      <SEOHead>
        <title>{pageTitleTag}</title>
        <meta name="description" content={metaDescription} />
        <link
          href="https://fonts.googleapis.com/css2?family=Roboto:wght@400;500&display=swap"
          rel="stylesheet"
        ></link>
        <script
          type="text/javascript"
          src="//app-ab33.marketo.com/js/forms2/js/forms2.min.js"
        />
      </SEOHead>
      <Header
        PaidCampaign={paid_campaign}
        AdditionalQuestions={additional_questions}
        leadValues={leadValues}
        phone_number={phone_number_setup}
        doElite={true}
        hideSchedulerHeader={hideSchedulerHeader}
      />
      <PricingBStyles>
        {/* Main Page Copy */}
        <div className="copy-container container">
          <h1 className="page-header">{packagesHeaderTitle}</h1>
          <h3
            className="sub-header"
            dangerouslySetInnerHTML={{
              __html: sanitizeHtml(heroSubtitle, sanitizeSettings),
            }}
          />

          <div className="row">
            <div className="col-12">
              <TPStyles>
                <div className="tp-container">
                  <TrustPilotComponent
                    templateID="5406e65db0d04a09e042d5fc"
                    height="28px"
                    alt
                  />
                </div>
              </TPStyles>
            </div>
          </div>
        </div>

        {/* Package Offerings */}
        <div className="packages-container container">
          {packages.map((card, index) => (
            <PricingCardStyles
              className={["card", card.title.toLowerCase()].join(" ")}
              key={index}
            >
              {card.title === "Corporation" && (
                <span className="popular">Most Popular</span>
              )}
              <img src={card.icon} alt={card.title} width="200px" height="200px" />
              <h3 className="font-weight-bold title">{card.title}</h3>
              <div className="short-line" />
              <div className="subheader">{card.description}</div>

              <div className="price mt-3">
                {card.price_prefix && (
                  <span className="superscript small">{card.price_prefix}</span>

                )}
                <span className="large">${card.price}</span>
                {card.price_suffix && (
                  <span className="small">{card.price_suffix}</span>
                )}
              </div>
              <div className="small">{card.subtitle}</div>
              <button
                type="button"
                className="btn btn-secondary btn-rounded mt-4"
                onClick={selectPackage}
              >
                Contact Us
              </button>
              <div className="line" />
              <ul className="checkmarks">
                {card.marketing_checkmarks.map((check, i) => (
                  <li key={i}>
                    <span>{check.title}</span>
                    <span className="checkmark-tooltip">
                      <ToolTip
                        styleText="checkmark-tooltip-text"
                        text={check.description}
                      />
                    </span>
                  </li>
                ))}
              </ul>
            </PricingCardStyles>
          ))}

          <PricingCardStyles className="costume-package">
            <h3 className="title">{costumePackage.title}</h3>
            <div className="subheader">{costumePackage.description}</div>

            <button
              type="button"
              className="btn btn-secondary btn-rounded mt-4 btn-lg"
              onClick={selectPackage}
            >
              Contact Us
            </button>
          </PricingCardStyles>
          <p className="disclaimer">*Prices subject to change based on state</p>
        </div>

        {/* About Services Section */}
        <section>
          <div className="container">
            {aboutServices &&
              aboutServices.map((item, index) => (
                <AboutServiceComponent
                  key={index}
                  data={item}
                  scrollUp={scrollUp}
                  whiteBg
                  smallTitles
                  customStyle="pricing"
                />
              ))}
          </div>
        </section>

        {/* View Pricing CTA */}
        <section className="container pt-0">
          <div className="row">
            <div className="col-md-12 text-center">
              <button
                onClick={scrollUp}
                type="button"
                className="btn btn-secondary btn-rounded"
              >
                View Pricing
              </button>
            </div>
          </div>
        </section>

        {/* Testimonial */}
        {testimonial && (
          <TestimonialComponent
            testimonial={testimonial}
            backgroundPosition="100px"
            whiteBackground
            showImageMobile={false}
          />
        )}

        {/* Value Props */}
        <section className="container value-props-container">
          <div className="row">
            <div className="col-md-12 text-center">
              <h3 className="title">{valueProps.title}</h3>
              <p className="subtitle">{valueProps.subtitle}</p>
              <div className="values-container">
                {valueProps.values.map((value, index) => (
                  <div className="value-prop" key={index}>
                    <img src={value.icon} alt={value.title} width="200px" height="200px" />
                    <h4 className="value-title">{value.title}</h4>
                    <p className="value-description">{value.description}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="faqs">
          <div className="container">
            <h3>{faqTitle}</h3>
            {faqs &&
              faqs.map((term, index) => (
                <div className="row" key={index}>
                  <Collapsible
                    trigger={term.question}
                    easing="ease-in-out"
                    transitionTime={200}
                  >
                    <div
                      className="answer"
                      dangerouslySetInnerHTML={{ __html: term.answer }}
                    />
                  </Collapsible>
                </div>
              ))}
          </div>
        </section>
      </PricingBStyles>

      {/* Email Newsletter */}
      <FooterNewsModal />

      {/* Footer */}
      <FooterComponent phone_number={phone_number_setup} />

      {currentPackage === "startup"
        ? isSchedulerOpen && (
          <PullUpScheduler
            setIsSchedulerOpen={selectPackage}
            PaidCampaign={paid_campaign}
            AdditionalQuestions={additional_questions}
            EntityCampaign="1"
            Team="entity"
            EForm={true}
          />
        )
        :
        currentPackage === "enterprise"
          ? isSchedulerOpen && (
            <PullUpScheduler
              setIsSchedulerOpen={selectPackage}
              PaidCampaign={paid_campaign}
              AdditionalQuestions={additional_questions}
              doElite={true}
              Team="elite"
            />
          )
          :
          isSchedulerOpen && (
            <PullUpScheduler
              setIsSchedulerOpen={selectPackage}
              PaidCampaign={paid_campaign}
              AdditionalQuestions={additional_questions}
              Team="default"
            />
          )}
    </>
  );
};

export const getStaticProps = async () => {
  const { data } = await axios.get(`${WordPressAPI.pages}/15168`);

  return {
    props: {
      data,
    },
    // revalidate: 1,
  };
};

export default PricingPage;
